import { useState, useEffect } from 'react';
import { Phone, PhoneCall, Clock } from 'lucide-react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import { formatUSPhoneNumber } from '../utils/phoneNumber';
import { settings } from '../config/settings';
import { TCPACompliance } from '../utils/tcpaCompliance';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { getBrowserTimezone } from '../utils/timezones';

interface PhoneDialerProps {
  phoneNumber: string;
  fallbackNumber?: string;
  autoTrigger?: boolean;
  onCallComplete?: () => void;
  onCallInitiated?: () => void;
  onCountdownComplete?: () => void; // New optional event
  onInitiateCall: () => Promise<{ success: boolean; from?: string; error?: string }>;
  duration?: number; // Countdown duration in seconds (default: 30)
}

export default function PhoneDialer({ 
  phoneNumber, 
  fallbackNumber = settings.phone.support,
  autoTrigger = false,
  onCallComplete,
  onCallInitiated,
  onCountdownComplete, // New prop
  onInitiateCall,
  duration = 30 // Default 30 seconds
}: PhoneDialerProps) {
  const { t } = useTranslation('common');
  const [isDialing, setIsDialing] = useState(false);
  const [callingFrom, setCallingFrom] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(duration || 30);
  const [nextAvailableTime, setNextAvailableTime] = useState<Date | null>(null);

  useEffect(() => {
    if (!TCPACompliance.isWithinCallHours()) {
      const next = TCPACompliance.getNextAvailableTime();
      setNextAvailableTime(next);
    }
  }, []);

  // Initiate call through parent component
  const initiateCall = async () => {
    try {
      const result = await onInitiateCall();
      
      if (result.success && result.from) {
        const formattedNumber = formatUSPhoneNumber(result.from);
        setCallingFrom(formattedNumber || result.from);
        if (onCallComplete) {
          onCallComplete();
        }
      } else {
        setError(result.error || t('phoneDialer.callUnableToConnect'));
        setIsDialing(false);
      }
    } catch (err) {
      console.error('Call initiation failed:', err);
      setError(t('phoneDialer.callConnectionFailed'));
      setIsDialing(false);
    }
  };

  useEffect(() => {
    if (autoTrigger && TCPACompliance.isWithinCallHours()) {
      setIsDialing(true);
    }
  }, [autoTrigger]);
  
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isDialing && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          const newCount = prev - 1;
          if (newCount === (duration || 30) - 5 && !callingFrom && !error) {
            initiateCall();
          }
          if (newCount === 1) {
            if (onCountdownComplete) {
              onCountdownComplete();
            }
            // If countdown finishes and no call was initiated, show error with fallback
            if (!callingFrom && !error) {
              setError(t('phoneDialer.callAutoConnectFailed'));
            }
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isDialing, callingFrom, countdown, onCountdownComplete, error]);

  // Start call when timer completes or button is clicked
  const handleStartCall = () => {
    if (!TCPACompliance.isWithinCallHours()) {
      const next = TCPACompliance.getNextAvailableTime();
      setNextAvailableTime(next);
      return;
    }

    setIsDialing(true);
    setError(null);
    setCountdown(30);
    if (onCallInitiated) {
      onCallInitiated();
    }
  };

  const formatNextAvailableTime = (date: Date): string => {
    const timezone = getBrowserTimezone();
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, "h:mm a 'local time'");
  };

  if (nextAvailableTime) {
    return (
      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 space-y-4">
        <div className="flex items-center gap-3 text-yellow-800">
          <Clock className="w-5 h-5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium">{t('phoneDialer.outsideHoursTitle')}</p>
            <p>{t('phoneDialer.outsideHoursDescription')}</p>
            <p className="mt-2">
              {t('phoneDialer.nextAvailableTime', { time: formatNextAvailableTime(nextAvailableTime) })}
            </p>
          </div>
        </div>
        <Button
          as="a"
          href={`tel:${settings.phone.supportRaw}`}
          variant="secondary"
          size="lg"
          fullWidth
          icon={<Phone className="w-5 h-5" />}
        >
          {fallbackNumber}
        </Button>
      </div>
    );
  }

  if (!isDialing) {
    return (
      <>
        {error && (
          <div className="mb-4 space-y-4">
            <div className="bg-yellow-50 text-black p-4 rounded-lg text-sm text-center border border-yellow-200">
              <p className="font-medium mb-2">{t('phoneDialer.callErrorTitle')}</p>
              <p className="text-xs mt-2">{t('phoneDialer.callErrorSubtitle')}</p>
            </div>
            <Button
              as="a"
              href={`tel:${settings.phone.supportRaw}`}
              variant="primary"
              size="lg"
              fullWidth
              icon={<Phone className="w-5 h-5" />}
            >
              {fallbackNumber}
            </Button>
          </div>
        )}
        {!error && (
          <Button
            onClick={handleStartCall}
            variant="primary"
            size="lg"
            fullWidth
            icon={<Phone className="w-5 h-5" />}
          >
            {t('phoneDialer.callMeNow')}
          </Button>
        )}
      </>
    );
  }

  return (
    <div className="bg-white p-2 space-y-2">
      <div className="text-center space-y-2">
        <h3 className="text-xl font-semibold text-gray-900">
          {t('phoneDialer.connectingTitle')}
        </h3>
        <p className="text-gray-600">
          {t('phoneDialer.connectingSubtitle')}
        </p>
      </div>

      <div className="flex flex-col items-center">
        <div className="relative">
          <CountdownCircleTimer
            isPlaying
            duration={duration}
            colors={'#b3905e' as const}
            size={160}
            strokeWidth={8}
          >
            {() => (
              <div className="flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-[#b3905e]/10 flex items-center justify-center animate-pulse">
                  <PhoneCall className="w-12 h-12 text-[#b3905e]" />
                </div>
              </div>
            )}
          </CountdownCircleTimer>
          
          {!callingFrom && !error && (
            <div className="text-center mt-6">
              <span className="text-4xl font-bold text-[#212d52]">{countdown}</span>
              <span className="text-sm text-gray-600 block">{t('phoneDialer.secondsLabel')}</span>
            </div>
          )}
        </div>
      </div>

      {callingFrom && (
        <div className="text-center space-y-2 bg-[#b3905e]/5 p-4 rounded-lg animate-fade-in">
          <p className="text-sm text-gray-600">
            {t('phoneDialer.incomingCallFrom')}
          </p>
          <a 
            href={`tel:${callingFrom.replace(/\D/g, '')}`}
            className="text-lg font-semibold text-[#b3905e] hover:text-[#9a7a4f] transition-colors underline decoration-2 underline-offset-2"
          >
            {callingFrom}
          </a> 
        </div>
      )}

      {error && (
        <div className="space-y-8 animate-fade-in">
          <div className="bg-yellow-50 text-black p-8 rounded-lg text-base text-center border-2 border-yellow-200">
            <p className="font-semibold text-lg mb-3">{t('phoneDialer.callErrorTitle')}</p>
            <p className="text-sm mt-3">{t('phoneDialer.callErrorSubtitle')}</p>
          </div>
          <Button
            as="a"
            href={`tel:${settings.phone.supportRaw}`}
            variant="primary"
            size="lg"
            fullWidth
            icon={<Phone className="w-5 h-5" />}
          >
            {fallbackNumber}
          </Button>
        </div>
      )}
    </div>
  );
}